import { useState, useEffect } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { useSites } from '../hooks/useSites';
import { formatNumber, formatPercent, formatDate } from '../lib/utils';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import type { Site, DailyStats, SiteStats, Verification } from '../types';

// Mock data
const mockDailyStats: DailyStats[] = [
  { date: '2026-02-16', total: 280, successful: 275, failed: 5 },
  { date: '2026-02-17', total: 320, successful: 315, failed: 5 },
  { date: '2026-02-18', total: 290, successful: 285, failed: 5 },
  { date: '2026-02-19', total: 350, successful: 345, failed: 5 },
  { date: '2026-02-20', total: 310, successful: 305, failed: 5 },
  { date: '2026-02-21', total: 340, successful: 335, failed: 5 },
  { date: '2026-02-22', total: 342, successful: 337, failed: 5 },
];

const mockHourlyStats = Array.from({ length: 24 }, (_, i) => ({
  hour: i,
  count: Math.floor(Math.random() * 50) + 10,
}));

const mockVerifications: Verification[] = [
  { id: '1', site_id: '1', status: 'success', ip_address: '192.168.1.1', user_agent: 'Chrome/120', created_at: '2026-02-22T10:30:00Z' },
  { id: '2', site_id: '1', status: 'success', ip_address: '10.0.0.5', user_agent: 'Firefox/121', created_at: '2026-02-22T10:28:00Z' },
  { id: '3', site_id: '1', status: 'failed', ip_address: '172.16.0.10', user_agent: 'Safari/17', created_at: '2026-02-22T10:25:00Z' },
  { id: '4', site_id: '1', status: 'success', ip_address: '192.168.1.50', user_agent: 'Chrome/120', created_at: '2026-02-22T10:20:00Z' },
  { id: '5', site_id: '1', status: 'success', ip_address: '10.0.0.15', user_agent: 'Edge/120', created_at: '2026-02-22T10:15:00Z' },
];

const mockSites: Site[] = [
  { id: '1', user_id: 'u1', domain: 'example.com', name: 'My Website', api_key: 'pk1', secret_key: 'sk1', is_active: true, allowed_origins: [], verification_count: 5432, created_at: '', updated_at: '' },
  { id: '2', user_id: 'u1', domain: 'myshop.com', name: 'Store', api_key: 'pk2', secret_key: 'sk2', is_active: true, allowed_origins: [], verification_count: 3210, created_at: '', updated_at: '' },
];

const COLORS = ['#10B981', '#EF4444'];

export function AnalyticsPage() {
  const { sites: apiSites, loading: sitesLoading } = useSites();
  const [sites, setSites] = useState<Site[]>([]);
  const [selectedSite, setSelectedSite] = useState<string>('');
  const [period, setPeriod] = useState<string>('7d');
  const [dailyStats] = useState<DailyStats[]>(mockDailyStats);
  const [siteStats, setSiteStats] = useState<SiteStats | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (apiSites.length > 0) {
      setSites(apiSites);
      if (!selectedSite) setSelectedSite(apiSites[0].id);
    } else if (!sitesLoading) {
      setSites(mockSites);
      if (!selectedSite) setSelectedSite(mockSites[0].id);
    }
  }, [apiSites, sitesLoading, selectedSite]);

  useEffect(() => {
    // Load stats when site or period changes
    if (selectedSite) {
      setLoading(true);
      // Simulate loading
      setTimeout(() => {
        const total = dailyStats.reduce((acc, d) => acc + d.total, 0);
        const successful = dailyStats.reduce((acc, d) => acc + d.successful, 0);
        setSiteStats({
          site_id: selectedSite,
          total_verifications: total,
          successful_verifications: successful,
          failed_verifications: total - successful,
          success_rate: (successful / total) * 100,
          verifications_by_hour: mockHourlyStats,
          recent_verifications: mockVerifications,
        });
        setLoading(false);
      }, 500);
    }
  }, [selectedSite, period, dailyStats]);

  const pieData = siteStats ? [
    { name: 'Successful', value: siteStats.successful_verifications },
    { name: 'Failed', value: siteStats.failed_verifications },
  ] : [];

  if (sitesLoading && sites.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground">
            Detailed verification statistics
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedSite} onValueChange={setSelectedSite}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Select site" />
            </SelectTrigger>
            <SelectContent>
              {sites.map((site) => (
                <SelectItem key={site.id} value={site.id}>
                  {site.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1d">Today</SelectItem>
              <SelectItem value="7d">7 Days</SelectItem>
              <SelectItem value="30d">30 Days</SelectItem>
              <SelectItem value="90d">90 Days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {sites.length === 0 && (
        <div className="flex items-center gap-2 p-4 text-sm text-yellow-600 bg-yellow-50 rounded-md">
          <AlertCircle className="h-4 w-4" />
          Add a site to view analytics.
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : siteStats && (
        <>
          {/* Stats summary */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total Verifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(siteStats.total_verifications)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Successful</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-success">{formatNumber(siteStats.successful_verifications)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Failed</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-error">{formatNumber(siteStats.failed_verifications)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPercent(siteStats.success_rate)}</div>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <Tabs defaultValue="daily" className="space-y-4">
            <TabsList>
              <TabsTrigger value="daily">By Day</TabsTrigger>
              <TabsTrigger value="hourly">By Hour</TabsTrigger>
              <TabsTrigger value="ratio">Success/Failed</TabsTrigger>
            </TabsList>

            <TabsContent value="daily">
              <Card>
                <CardHeader>
                  <CardTitle>Daily Verifications</CardTitle>
                  <CardDescription>Verification trend for selected period</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={dailyStats}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis
                          dataKey="date"
                          tickFormatter={(v) => new Date(v).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(v) => formatDate(v)}
                          formatter={(v: number, name: string) => [formatNumber(v), name === 'successful' ? 'Successful' : 'Failed']}
                          contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))' }}
                        />
                        <Legend formatter={(v) => v === 'successful' ? 'Successful' : 'Failed'} />
                        <Line type="monotone" dataKey="successful" stroke="#10B981" strokeWidth={2} />
                        <Line type="monotone" dataKey="failed" stroke="#EF4444" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="hourly">
              <Card>
                <CardHeader>
                  <CardTitle>Hourly Verifications</CardTitle>
                  <CardDescription>Distribution throughout the day</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={siteStats.verifications_by_hour}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                        <XAxis dataKey="hour" tickFormatter={(v) => `${v}:00`} />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(v) => `${v}:00 - ${v}:59`}
                          formatter={(v: number) => [formatNumber(v), 'Verifications']}
                          contentStyle={{ backgroundColor: 'hsl(var(--background))', borderColor: 'hsl(var(--border))' }}
                        />
                        <Bar dataKey="count" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="ratio">
              <Card>
                <CardHeader>
                  <CardTitle>Success / Failed Ratio</CardTitle>
                  <CardDescription>Distribution of successful vs failed verifications</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[350px] flex items-center justify-center">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={80}
                          outerRadius={120}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                        >
                          {pieData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v: number) => formatNumber(v)} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Recent verifications table */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Verifications</CardTitle>
              <CardDescription>Detailed information about recent verifications</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>IP Address</TableHead>
                    <TableHead>User Agent</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {siteStats.recent_verifications.map((v) => (
                    <TableRow key={v.id}>
                      <TableCell>{formatDate(v.created_at, { hour: '2-digit', minute: '2-digit' })}</TableCell>
                      <TableCell>
                        <Badge variant={v.status === 'success' ? 'success' : 'destructive'}>
                          {v.status === 'success' ? 'Success' : 'Failed'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{v.ip_address}</TableCell>
                      <TableCell className="text-muted-foreground">{v.user_agent}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
